import React, { JSX, useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import styles from "../../../scss/pagination-styles.module.scss";

interface CustomPaginationProps {
    pageCount: number;
    forcePage?: number;
    currentPageNumber?: number; 
    onPageChange: (selectedItem: { selected: number }) => void;
    marginPagesDisplayed?: number;
    pageRangeDisplayed?: number;
    previousLabel?: string | React.ReactNode;
    nextLabel?: string | React.ReactNode;
    renderOnZeroPageCount?: JSX.Element | null;
    containerClassName?: string;
    pageClassName?: string;
    activeClassName?: string;
    disabledClassName?: string;
    breakLabel?: React.ReactNode | string;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
    pageCount,
    forcePage = 0,
    currentPageNumber, // NEW
    onPageChange,
    marginPagesDisplayed = 2,
    pageRangeDisplayed = 4,
    renderOnZeroPageCount,
    containerClassName = styles.pagination_items,
    pageClassName = styles.page_num,
    activeClassName = styles.active_page_link,
    disabledClassName = styles.disabled,
    breakLabel = "..."
}) => {
    const [isJumpInputVisible, setIsJumpInputVisible] = useState(false);
    const [jumpValue, setJumpValue] = useState("");
    const [currentPage, setCurrentPage] = useState(forcePage);

    // Keep currentPage synced with currentPageNumber or forcePage changes
    useEffect(() => {
        if (typeof currentPageNumber === "number") {
            setCurrentPage(currentPageNumber - 1); // convert 1-based to 0-based
        } else {
            setCurrentPage(forcePage);
        }
    }, [currentPageNumber, forcePage]);

    const pages = useMemo(() => {
        const items: (number | string)[] = [];
        const leftSide = Math.floor(pageRangeDisplayed / 2);
        const rightSide = pageRangeDisplayed - leftSide;

        let startPage = Math.max(currentPage - leftSide, 0);
        let endPage = Math.min(currentPage + rightSide - 1, pageCount - 1);

        // Add left margin pages
        for (let i = 0; i < Math.min(marginPagesDisplayed, startPage); i++) {
            items.push(i);
        }

        if (startPage > marginPagesDisplayed) {
            items.push(breakLabel as string);
        }

        // Add main page range
        for (let i = startPage; i <= endPage; i++) {
            items.push(i);
        }

        // Add right margin pages
        if (endPage < pageCount - marginPagesDisplayed - 1) {
            items.push(breakLabel as string);
        }
        for (let i = Math.max(pageCount - marginPagesDisplayed, endPage + 1); i < pageCount; i++) {
            items.push(i);
        }

        return items;
    }, [pageCount, currentPage, marginPagesDisplayed, pageRangeDisplayed, breakLabel]);

    if (pageCount <= 0) {
        return renderOnZeroPageCount || null;
    }

    const handleJumpSubmit = () => {
        const pageNum = parseInt(jumpValue, 10);
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= pageCount) {
            onPageChange({ selected: pageNum - 1 });
            setCurrentPage(pageNum - 1);
        }
        setJumpValue("");
    };

    return (
        <ul className={`${containerClassName}`}>
            {/* Previous */}
            <li
                className={`${styles.previous_page_link} ${currentPage === 0 ? disabledClassName : ""}`}
                onClick={() => currentPage > 0 && onPageChange({ selected: currentPage - 1 })}
            >
                <><FaArrowLeft /> <span>Prev</span></>
            </li>

            {/* Page Numbers */}
            {pages.map((page, idx) =>
                page === breakLabel ? (
                    <li key={idx} className={styles.break}>
                        {isJumpInputVisible ? (
                            <input
                                type="number"
                                min={1}
                                max={pageCount}
                                value={jumpValue}
                                onChange={(e) => setJumpValue(e.target.value)}
                                onBlur={handleJumpSubmit}
                                onKeyDown={(e) => e.key === "Enter" && handleJumpSubmit()}
                                autoFocus
                            />
                        ) : (
                            <span onClick={() => setIsJumpInputVisible(true)}>
                                {breakLabel}
                            </span>
                        )}
                    </li>
                ) : (
                    <li
                        key={idx}
                        className={`${pageClassName} ${page === currentPage ? activeClassName : ""}`}
                        onClick={() => onPageChange({ selected: page as number })}
                    >
                        <a>{(page as number) + 1}</a>
                    </li>
                )
            )}

            {/* Next */}
            <li
                className={`${styles.next_page_link} ${currentPage === pageCount - 1 ? disabledClassName : ""}`}
                onClick={() => currentPage < pageCount - 1 && onPageChange({ selected: currentPage + 1 })}
            >
                <><span>Next</span> <FaArrowRight /></>
            </li>
        </ul>
    );
};


export default CustomPagination;
